import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
} from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import _, { isFunction } from 'lodash';
import Toolbar from '../Toolbar';
import { BS_TOOLBAR_STATUS } from '../../../../comm/constant';
import { bedStatusAPI } from '../../../../apis';
import { useActionableTicketReminder } from '../../../../utils/hooks';
import { BedStatusStatisticContext, bedstatusRef } from '../..';
import { calculateRemToPx } from '../../../../utils/newBedStatusUtil';
import StatusBar from './StatusBar';
import QueueBar from './QueueBar';

const FloorPlan = lazy(() => import('./FloorPlan'));

const useStyles = makeStyles(() => ({
  root: ({ toolStatus, fullScreen, remarkBar, sso }) => {
    let style = {
      transform: `scale(${toolStatus.zoom})`,
      transformOrigin: 'left top',
      cursor: 'auto',
      userSelect: 'auto',
    };

    if (toolStatus.type === BS_TOOLBAR_STATUS.MOUSE) {
      style.cursor = 'auto';
      style.userSelect = 'auto';
    } else if (toolStatus.type === BS_TOOLBAR_STATUS.MOVE) {
      style.cursor = 'grab';
      style.userSelect = 'none';
    }

    if (fullScreen || sso) {
      if (remarkBar) {
        style.maxHeight = `calc(100vh - 4.8rem - 15.2rem)`;
      } else {
        style.maxHeight = `calc(100vh - 3.75rem)`;
      }
    } else if (remarkBar) {
      style.maxHeight = `calc(100vh - 10.125rem - 15.2rem)`;
    } else {
      style.maxHeight = `calc(100vh - 10.125rem)`;
    }

    return style;
  },
  leftContainer: () => ({
    display: 'flex',
    position: 'sticky',
    left: 0,
    top: 0,
    zIndex: 10,
  }),
}));

const BED_STATUS_FLOORPLAN_ZOOM_STORAGE_KEY = 'BED_STATUS_FLOORPLAN_ZOOM';
const DEFAULT_FLOORPLAN_ZOOM = 1;
const SHOW_PATIENT_NAME_ZOOM_STEP = 0.2;

const getStoredFloorplanZoom = () => {
  const storageValue = sessionStorage.getItem(BED_STATUS_FLOORPLAN_ZOOM_STORAGE_KEY);
  if (!storageValue) {
    return DEFAULT_FLOORPLAN_ZOOM;
  }
  const zoom = Number(storageValue);
  if (!Number.isFinite(zoom) || zoom < 0.5 || zoom > 2) {
    return DEFAULT_FLOORPLAN_ZOOM;
  }
  return zoom;
};

const Landing = ({
  headerRef,
  filter,
  handleFilterSelectOption,
  statusBottom,
  remarks,
  filterData,
  handleRouteToTransferIn,
  showPatientName,
}) => {
  const fullScreen = useSelector((state) => state.fullScreenReducer);
  const sso = useSelector((state) => state.ssoReducer);
  const { isCubicleV2 } = useSelector((state) => state.bedStatusView);
  const [toolStatus, setToolStatus] = useState({
    type: BS_TOOLBAR_STATUS.MOVE,
    zoom: getStoredFloorplanZoom() + (showPatientName ? SHOW_PATIENT_NAME_ZOOM_STEP : 0),
    reset: 0,
  });
  const [remarkBar, setRemarkBar] = useState(false);
  const classes = useStyles({ toolStatus, fullScreen, remarkBar, sso });
  const WardBoxRef = useRef(null);
  // 使用 useRef 存储拖拽信息，避免作为依赖项导致页面一直刷新
  const isDraggingRef = useRef(false);
  const startPositionRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const prevShowPatientNameRef = useRef(showPatientName);
  const skipZoomStorageRef = useRef(false);
  const [floorPlan, setFloorPlan] = useState([]);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(0);
  const [popup, setPopup] = useState(false);
  const rouList = useSelector((state) => state.rouList);
  const { refresh } = useContext(BedStatusStatisticContext);
  useActionableTicketReminder('msg', 'BS');
  const [bedStatusQueueList, setBedStatusQueueList] = useState(null);
  const [BsMlookupList, setBsMlookupList] = useState(null);
  const [showQueueBar, setShowQueueBar] = useState(
    JSON.parse(sessionStorage.getItem('SHOW_BED_STATUS_QUEUE_BAR')) ?? true
  );
  const statusBarRef = useRef(null);
  const queueBarRef = useRef(null);
  // floor plan剩余可用高度
  const [floorPlanHeight, setFloorPlanHeight] = useState(0);

  useEffect(() => {
    WardBoxRef.current.scroll(0, 0);
  }, [toolStatus.reset]);

  // 下面的各种鼠标和触摸事件会拦截点击事件，当事件是来自QueueBar的时候也不拦截
  const isQueueBarClickEvent = useCallback(
    (event) => {
      if (queueBarRef.current && queueBarRef.current.contains(event.target)) {
        // 在QueueBar内部，直接return，不做拖拽处理
        return true;
      }
      return false;
    },
    [queueBarRef]
  );

  const handleMouseDown = useCallback(
    (event) => {
      const isPopup = document.getElementsByClassName('MuiDialog-root').length > 0;
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || popup || isPopup || isQueueBarClickEvent(event)) {
        return;
      }
      event.preventDefault();
      isDraggingRef.current = true;
      const position = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: WardBoxRef.current.scrollLeft,
        scrollTop: WardBoxRef.current.scrollTop,
      };
      startPositionRef.current = position;
    },
    [toolStatus, popup, isQueueBarClickEvent]
  );

  const handleTouchStart = useCallback(
    (event) => {
      const isPopup = document.getElementsByClassName('MuiDialog-root').length > 0;
      if (
        toolStatus.type !== BS_TOOLBAR_STATUS.MOVE ||
        popup ||
        event.changedTouches.length > 1 ||
        isPopup ||
        isQueueBarClickEvent(event)
      ) {
        return;
      }
      event.preventDefault();
      isDraggingRef.current = true;
      const position = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
        scrollLeft: WardBoxRef.current.scrollLeft,
        scrollTop: WardBoxRef.current.scrollTop,
      };
      startPositionRef.current = position;
    },
    [toolStatus, popup, isQueueBarClickEvent]
  );

  const handleMouseMove = useCallback(
    (event) => {
      const isPopup = document.getElementsByClassName('MuiDialog-root').length > 0;
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || popup || isPopup || isQueueBarClickEvent(event)) {
        return;
      }
      // 使用 ref 检查拖拽状态，避免依赖 state 导致函数重新创建
      if (isDraggingRef.current && WardBoxRef.current) {
        event.preventDefault();
        const startPos = startPositionRef.current;
        const deltaX = event.clientX - startPos.x;
        const deltaY = event.clientY - startPos.y;
        const scrollLeft = startPos.scrollLeft - deltaX;
        const scrollTop = startPos.scrollTop - deltaY;
        WardBoxRef.current.scroll(scrollLeft, scrollTop);
      }
    },
    [toolStatus, popup, isQueueBarClickEvent]
  );

  const handleTouchMove = useCallback(
    (event) => {
      const isPopup = document.getElementsByClassName('MuiDialog-root').length > 0;
      if (
        toolStatus.type !== BS_TOOLBAR_STATUS.MOVE ||
        popup ||
        event.changedTouches.length > 1 ||
        isPopup ||
        isQueueBarClickEvent(event)
      ) {
        return;
      }
      // 使用 ref 检查拖拽状态，避免依赖 state 导致函数重新创建
      if (isDraggingRef.current && WardBoxRef.current) {
        event.preventDefault();
        const startPos = startPositionRef.current;
        const deltaX = event.changedTouches[0].clientX - startPos.x;
        const deltaY = event.changedTouches[0].clientY - startPos.y;
        const scrollLeft = startPos.scrollLeft - deltaX;
        const scrollTop = startPos.scrollTop - deltaY;
        WardBoxRef.current.scroll(scrollLeft, scrollTop);
      }
    },
    [toolStatus, popup, isQueueBarClickEvent]
  );

  const handleMouseUp = useCallback(() => {
    if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE) {
      return;
    }
    isDraggingRef.current = false;
  }, [toolStatus]);

  const handleMouseLeave = useCallback(() => {
    if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE) {
      return;
    }
    isDraggingRef.current = false;
  }, [toolStatus]);

  const handleRemarkBar = useCallback(() => {
    setRemarkBar((o) => !o);
  }, []);

  const getFloorPlanListAPI = useCallback(async (callback) => {
    try {
      const { result } = await bedStatusAPI.getFloorPlanList();
      setFloorPlan(result?.floorplans);
      setBedStatusQueueList(result?.queuebar);
      setLastUpdatedTime(
        result?.floorplans?.reduce((maxValue, currentItem) => {
          if (currentItem.lastUpdateDate > maxValue) {
            return currentItem.lastUpdateDate;
          } else {
            return maxValue;
          }
        }, 0)
      );
      if (isFunction(callback)) {
        callback();
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  // 监听 window resize 和 ref 变化
  useEffect(() => {
    // 计算并更新高度
    const calculatedHeight = () => {
      try {
        // 页面高度
        const pageHeight = document.documentElement?.clientHeight || window.innerHeight || 0;
        // bed status header 高度
        const headerHeight = headerRef?.current ? headerRef.current.getBoundingClientRect().height : 0;
        // bed status底部status bar 高度
        const statusBarHeight = statusBarRef?.current ? statusBarRef.current.getBoundingClientRect().height : 0;
        const availableHeight = Math.round(
          pageHeight - headerHeight - statusBarHeight - (sso || fullScreen ? 0 : calculateRemToPx(4.375)) - 5
        );
        setFloorPlanHeight(availableHeight);
      } catch {
        setFloorPlanHeight(0);
      }
    };

    calculatedHeight();
    window.addEventListener('resize', calculatedHeight);

    return () => {
      window.removeEventListener('resize', calculatedHeight);
    };
  }, [remarkBar, headerRef, statusBarRef, fullScreen]);

  useImperativeHandle(bedstatusRef, () => ({
    reload: getFloorPlanListAPI,
  }));

  useEffect(() => {
    getFloorPlanListAPI();

    const refresh = setInterval(() => {
      const dialogs = document.getElementsByClassName('MuiDialog-container');
      if (dialogs.length === 0) {
        getFloorPlanListAPI();
      }
    }, 3 * 60 * 1000);

    return () => {
      clearInterval(refresh);
    };
  }, [getFloorPlanListAPI, rouList, refresh]);

  useEffect(() => {
    setRemarkBar(false);
  }, [fullScreen]);

  const floorMemo = useMemo(() => {
    if (filter.wardLocation === 'All') {
      return _.orderBy(floorPlan, 'wardLocation');
    } else {
      return _.filter(floorPlan, (item) => item.wardLocation === filter.wardLocation);
    }
  }, [floorPlan, filter]);

  const getBsMlookupList = async () => {
    const getBsMlookupList = await bedStatusAPI.getBsMlookupList();
    setBsMlookupList(getBsMlookupList?.result);
  };

  useEffect(() => {
    getBsMlookupList();
  }, [refresh, rouList]);

  useEffect(() => {
    if (prevShowPatientNameRef.current === showPatientName) {
      return;
    }
    prevShowPatientNameRef.current = showPatientName;
    skipZoomStorageRef.current = true;

    // 显示名字的时候自动放大0.2,退出的时候恢复正常
    setToolStatus((prev) => {
      let zoomVal = prev?.zoom;
      if (showPatientName) {
        zoomVal += SHOW_PATIENT_NAME_ZOOM_STEP;
      } else {
        zoomVal = zoomVal === DEFAULT_FLOORPLAN_ZOOM ? zoomVal : (zoomVal -= SHOW_PATIENT_NAME_ZOOM_STEP);
      }
      return {
        ...prev,
        zoom: zoomVal,
      };
    });
  }, [showPatientName]);

  useEffect(() => {
    if (skipZoomStorageRef.current) {
      skipZoomStorageRef.current = false;
      return;
    }
    const zoom = showPatientName ? toolStatus.zoom - SHOW_PATIENT_NAME_ZOOM_STEP : toolStatus.zoom;
    if (zoom >= 0.5 && zoom <= 2) {
      sessionStorage.setItem(BED_STATUS_FLOORPLAN_ZOOM_STORAGE_KEY, String(zoom));
    }
  }, [showPatientName, toolStatus.zoom]);

  return (
    <>
      <Box
        display="flex"
        padding="0.8rem 0rem 0rem 0rem"
        gridGap="1rem"
        ref={WardBoxRef}
        overflow="hidden"
        height={floorPlanHeight}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
        onTouchCancel={handleMouseLeave}
        style={{ touchAction: 'none' }}
      >
        <Box className={classes.leftContainer}>
          {isCubicleV2 && (
            <QueueBar
              ref={queueBarRef}
              showPatientName={showPatientName}
              bedStatusQueueList={bedStatusQueueList}
              getFloorPlanListAPI={getFloorPlanListAPI}
              BsMlookupList={BsMlookupList}
              showRemarkBar={remarkBar}
              showQueueBar={showQueueBar}
              setShowQueueBar={setShowQueueBar}
              filter={filter}
            />
          )}
          <Toolbar setStatus={setToolStatus} status={toolStatus} isCubicleV2={isCubicleV2} />
        </Box>
        <Box className={classes.root} display="flex" flexDirection="column" gridGap="2rem">
          {floorMemo.map((item) => (
            <Suspense fallback={<div />} key={item.hospCode + item.wardLocation}>
              <FloorPlan
                allData={item}
                getFloorPlanListAPI={getFloorPlanListAPI}
                setPopup={setPopup}
                filterData={filterData}
                handleRouteToTransferIn={handleRouteToTransferIn}
                showPatientName={showPatientName}
                queueBarRef={queueBarRef}
                showQueueBar={showQueueBar}
              />
            </Suspense>
          ))}
        </Box>
      </Box>
      <StatusBar
        ref={statusBarRef}
        open={remarkBar}
        handleRemarkBar={handleRemarkBar}
        lastUpdatedTime={lastUpdatedTime}
        handleFilterSelectOption={handleFilterSelectOption}
        statusBottom={statusBottom}
        remarks={remarks}
      />
    </>
  );
};

export default Landing;
